import { useReactiveVar } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import { Editor } from "@tiptap/core";
import {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  MeetingViewMeetingNodeNewPageFragmentFragment,
  TopicNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import { currentTiptapJWTVar } from "@cache/cache";
import Error from "@components/error/error";
import Loading from "@components/loading/loading";
import {
  destroyYDocAndWebsocketProvider,
  getYDocAndWebsocketProvider,
  isEmptyValue,
} from "@components/wysiwyg/helpers";
import WYSIWYG from "@components/wysiwyg/wysiwyg";
import { tempCacheIdPrefix } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { parseStringToJSON } from "@helpers/helpers";

import { MeetingWebsocketProviderContext } from "../context";

const DiscussionNotes = ({
  topic,
  meeting,
  className,
  onModEnter,
  onEnter,
  onEmptyChange,
  onWebsocketMessageChange,
  occurrence,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  className?: string;
  onModEnter?: () => void;
  onEnter?: () => void;
  onEmptyChange?: (isEmpty: boolean) => void;
  onWebsocketMessageChange?: (message: string | null) => void;
  occurrence: MeetingViewMeetingNodeNewPageFragmentFragment["agendaTopics"][number];
}) => {
  const isTemporary = String(topic.id).includes(tempCacheIdPrefix);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const topicValue = parseStringToJSON(topic.discussionNotes);
  let overlayValue = null;
  if (topic.linkedTemplateTopic?.id) {
    if (isEmptyValue(topicValue) && topic.linkedTemplateTopic.discussionNotes) {
      overlayValue = parseStringToJSON(
        topic.linkedTemplateTopic.discussionNotes,
      );
    }
  }

  const meetingGroupId = meeting.meetingGroup?.id;
  const organizationId = meeting.organization?.id;
  const uploadVariable = useMemo(() => ({ topicId: topic.id }), [topic.id]);

  const mentionsConfig = useMemo(
    () => ({
      meetingGroupId: meetingGroupId,
      meetingId: meeting.id,
    }),
    [meeting.id, meetingGroupId],
  );
  const extraContext = useMemo(
    () => ({
      topicId: topic.id,
      relatedTopicId: topic.id,
      meetingId: meeting.id,
      meetingDate: meeting.startDatetime || undefined,
      meetingGroupId: meetingGroupId || undefined,
      organizationId: organizationId,
    }),
    [
      meeting.id,
      topic.id,
      meetingGroupId,
      meeting.startDatetime,
      organizationId,
    ],
  );

  const { sharedNotes: meetingSharedNotes } = useContext(
    MeetingWebsocketProviderContext,
  );
  const pinnedSharedNotes = useMemo(
    () =>
      occurrence.isPinned && occurrence.editorWebsocketToken
        ? getYDocAndWebsocketProvider(
            occurrence.editorDocumentName,
            occurrence.editorWebsocketToken,
          )
        : null,
    [
      occurrence.editorDocumentName,
      occurrence.editorWebsocketToken,
      occurrence.isPinned,
    ],
  );
  useEffect(
    () => () => {
      if (pinnedSharedNotes) {
        destroyYDocAndWebsocketProvider(occurrence.editorDocumentName);
      }
    },
    [occurrence.editorDocumentName, pinnedSharedNotes],
  );
  const sharedNotes = pinnedSharedNotes || meetingSharedNotes;
  const tiptapAiJwt = useReactiveVar(currentTiptapJWTVar);

  const handleUpdateContent = useCallback(
    ({ editor }: { editor: any }) => {
      const isEmpty = isEmptyValue(editor.getJSON());
      onEmptyChange?.(isEmpty);
    },
    [onEmptyChange],
  );

  const focusEditorAtEnd = () => {
    const el = containerRef.current?.querySelector(".ProseMirror") as
      | (HTMLDivElement & { editor: Editor })
      | null;
    if (el?.editor) {
      el.editor.chain().focus("end").run();
    }
  };

  return (
    <div
      aria-label="Meeting topic shared notes"
      className="relative"
      ref={containerRef}
      onClick={(e: MouseEvent<HTMLDivElement>) => {
        const target = e.target as Node | null;
        const proseMirrorEl =
          containerRef.current?.querySelector(".ProseMirror") ?? null;
        if (!proseMirrorEl || !target || !proseMirrorEl.contains(target)) {
          focusEditorAtEnd();
        }
      }}
    >
      <div>
        {isTemporary ? (
          <div className="min-h-8 flex items-center justify-center">
            <Loading size="5" />
          </div>
        ) : (
          <ErrorBoundary
            fallback={
              <Error
                title="An unexpected error occurred."
                description={
                  "The editor could not be rendered. We have been notified. Please refresh the page."
                }
              />
            }
          >
            <div
              id={`editor-topic-${topic.id}`}
              // Do not remove the key with tiptapAiJwt, editor needs to be recreated when the tiptapAiJwt changes
              key={`${tiptapAiJwt}`}
            >
              <WYSIWYG
                key={`topic-${topic.id}`}
                value={topicValue}
                enableComment={occurrence.canUpdate.permission}
                className={classNames(
                  "text-sm",
                  // Keep the text visually flush with the topic title while
                  // extending the editor's bounding box 28px to the left.
                  // The drag-handle plugin hides on the editor's mouseleave,
                  // so the gutter where the handle appears needs to be part
                  // of the editor's hit region — otherwise the handle
                  // disappears as the cursor crosses into it. `pl-7`
                  // (default from WYSIWYG) gives the inside-padding gutter,
                  // and `-ml-7` shifts the whole element back so text stays
                  // in place. `max-w-none` overrides the WYSIWYG default's
                  // `max-w-full` so the box can extend 28px wider to
                  // compensate for the leftward shift — otherwise the
                  // right edge stops 28px short of the container.
                  "-ml-7 max-w-none",
                  // Block spacing inside topic notes editors is handled
                  // globally in index.css under `.js-meeting-topic .ProseMirror`
                  // (uniform 10px between any two adjacent blocks).
                  className,
                  "js-meeting-topic-input",
                )}
                ydoc={sharedNotes.ydoc}
                providerWebsocket={sharedNotes.providerWebsocket}
                editable={occurrence.canUpdate.permission}
                placeholder="Add notes, '/' for commands"
                showPlusButton={occurrence.canUpdate.permission}
                uploadVariable={uploadVariable}
                overlayValue={overlayValue}
                webSocketDocumentId={`topic-${topic.id}`}
                mentionsConfig={mentionsConfig}
                extraContext={extraContext}
                tiptapAiJwt={tiptapAiJwt}
                onUpdateContent={handleUpdateContent}
                onModEnter={onModEnter}
                onEnter={onEnter}
                hideStatusBanner
                onWebsocketMessageChange={onWebsocketMessageChange}
              />
            </div>
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
};

export default DiscussionNotes;
